import {
  createContext,
  useContext,
  useMemo,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import {
  styled,
  ToggleButtonGroup,
  ToggleButton,
  toggleButtonClasses,
  Badge,
} from "@mui/material";
import type { SearchScope } from "../lib/types/types";

type Color = "primary" | "secondary";

type ToggleButtonGroupContextValue = {
  color: Color;
};

const ToggleButtonGroupContext = createContext<ToggleButtonGroupContextValue>({
  color: "primary",
});

const StyledToggleButton = styled(ToggleButton)(({ color }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "calc(var(--spacing)*2)",
  border: "none !important",
  textTransform: "none",
  color: `var(${color === "secondary" ? `--text-primary` : "--color-white"}) !important`,
  fontWeight: "400 !important",
  fontSize: "16px",
  letterSpacing: "0.15px",
  lineHeight: "22px",
  borderRadius: "var(--radius-2xl) !important",
  margin: "0 !important",
  padding: "calc(var(--spacing)*2) !important",
  width: "64px",
  [`&.${toggleButtonClasses.selected}`]: {
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#0000000A",
    },
  },
  [`& svg:not([data-highlight])`]: {
    borderRadius: "var(--radius-2xl)",
    backgroundColor: `${color === "secondary" ? `var(--color-white)` : "#F2F2F22B"}`,
    color: `var(${color === "secondary" ? `--text-tertiary` : "--color-white"})`,
    transition: "background-color 0.3s, color 0.3s",
  },
  [`&.${toggleButtonClasses.selected} svg:not([data-highlight])`]: {
    backgroundColor: `var(${color === "secondary" ? `--color-surface-accent-strong` : "--color-white"})`,
    color: `var(${color === "secondary" ? `--color-white` : "--color-surface-accent-strong"})`,
  },
  [`& [data-highlight]`]: {
    opacity: "1",
    transition: "opacity 0.3s",
  },
  [`&.${toggleButtonClasses.selected} [data-highlight]`]: {
    opacity: "0",
  },
}));

type ScopeSwitcherButtonProps = PropsWithChildren<{
  value: SearchScope;
  badgeContent?: ReactNode;
}>;

export const ScopeSwitcherButton = ({
  value,
  badgeContent,
  children,
}: ScopeSwitcherButtonProps) => {
  const { color } = useContext(ToggleButtonGroupContext);

  const button = (
    <StyledToggleButton value={value} color={color}>
      {children}
    </StyledToggleButton>
  );

  return badgeContent ? (
    <Badge
      color="primary"
      badgeContent={badgeContent}
      sx={{
        ".MuiBadge-badge": {
          background: "linear-gradient(103deg, #93FFD4 0%, #0F9 100%)",
          px: "calc(var(--spacing)*2)",
          py: "calc(var(--spacing)*0.5)",
          color: "var(--color-text-interactive)",
          fontWeight: 400,
          fontSize: "var(--text-xs)",
          lineHeight: "var(--tw-leading, var(--text-xs--line-height))",
          height: "auto",
          top: "calc(var(--spacing)*2)",
          insetInlineEnd: "calc(var(--spacing)*2)",
        },
      }}
    >
      {button}
    </Badge>
  ) : (
    button
  );
};

type Props = PropsWithChildren<{
  value: SearchScope;
  onChange: (newValue: SearchScope | null) => void;
  color: Color;
  hidden?: boolean;
}>;

export const ScopeSwitcherBase = ({
  value,
  onChange,
  color,
  hidden,
  children,
}: Props) => {
  const contextValue = useMemo(() => ({ color }), [color]);

  return (
    <ToggleButtonGroupContext value={contextValue}>
      <ToggleButtonGroup
        color={color}
        value={value}
        exclusive
        onChange={(_, newValue: SearchScope | null) => {
          onChange(newValue);
        }}
        aria-label="Platform"
        sx={{
          display: "flex",
          gap: "calc(var(--spacing)*2)",
          visibility: hidden ? "hidden" : "visible",
        }}
      >
        {children}
      </ToggleButtonGroup>
    </ToggleButtonGroupContext>
  );
};
